import { CheckCircle } from "@mui/icons-material";
import { Box, Button, Option, Select, Stack, Textarea, Typography } from "@mui/joy";
import { useEffect, useState } from "react";

const API_URL = process.env.REACT_APP_API_URL!.replace(/\/$/, "");
const FALLBACK_BRANCHES = ["Онлайн", "Пушкинская", "Грушевка", "Восток", "Немига/Октябрьская"];

type State = "idle" | "submitting" | "done" | "error";

const lightWrapper = {
  "data-joy-color-scheme": "light",
  style: { colorScheme: "light" },
} as const;

export default function FeedbackPage() {
  const [branches, setBranches] = useState<string[]>([]);
  const [score, setScore] = useState<number | null>(null);
  const [branch, setBranch] = useState<string | null>(null);
  const [review, setReview] = useState("");
  const [contact, setContact] = useState("");
  const [state, setState] = useState<State>("idle");

  useEffect(() => {
    fetch(`${API_URL}/api/feedback/branches`)
      .then((r) => r.json())
      .then((d) => setBranches(d.branches ?? FALLBACK_BRANCHES))
      .catch(() => setBranches(FALLBACK_BRANCHES));
  }, []);

  const canSubmit = score !== null && branch !== null && state === "idle";

  const submit = async () => {
    if (!canSubmit) return;
    setState("submitting");
    try {
      const res = await fetch(`${API_URL}/api/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ score, branch, review, contact }),
      });
      if (!res.ok) throw new Error("server error");
      setState("done");
    } catch {
      setState("error");
    }
  };

  if (state === "done") {
    return (
      <Box
        {...lightWrapper}
        sx={{
          minHeight: "100dvh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          px: 3,
          textAlign: "center",
          bgcolor: "background.body",
        }}
      >
        <CheckCircle sx={{ fontSize: 64, color: "primary.400" }} />
        <Typography level="h3">Спасибо за отзыв!</Typography>
        <Typography level="body-md" color="neutral">
          Мы читаем каждый и стараемся становиться лучше 💜
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      {...lightWrapper}
      sx={{
        minHeight: "100dvh",
        bgcolor: "background.body",
        display: "flex",
        flexDirection: "column",
        px: 3,
        py: 5,
        maxWidth: 480,
        mx: "auto",
        gap: 4,
      }}
    >
      <Box component="img" src="/logo192.png" alt="AMANI" sx={{ width: 56, height: 56 }} />

      <Typography level="h3" sx={{ lineHeight: 1.3 }}>
        Оцените ваш опыт взаимодействия с AMANI
      </Typography>

      <Stack gap={3}>
        <Box>
          <Typography level="title-sm" mb={1}>
            По шкале от 1 до 10 *
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
              <Button
                key={n}
                variant={score === n ? "solid" : "outlined"}
                color={score === n ? "primary" : "neutral"}
                size="sm"
                onClick={() => setScore(n)}
                sx={{ minWidth: 44, fontWeight: score === n ? "bold" : "normal" }}
              >
                {n}
              </Button>
            ))}
          </Box>
        </Box>

        <Box>
          <Typography level="title-sm" mb={1}>
            Филиал *
          </Typography>
          <Select
            placeholder="Выберите филиал"
            value={branch}
            onChange={(_, v) => setBranch(v)}
            sx={{ width: "100%" }}
          >
            {branches.map((b) => (
              <Option key={b} value={b}>
                {b}
              </Option>
            ))}
          </Select>
        </Box>

        <Box>
          <Typography level="title-sm" mb={1}>
            Комментарий
          </Typography>
          <Textarea
            placeholder="Расскажите подробнее..."
            minRows={3}
            value={review}
            onChange={(e) => setReview(e.target.value)}
          />
        </Box>

        <Box>
          <Typography level="title-sm" mb={0.5}>
            Контакт для связи
          </Typography>
          <Typography level="body-xs" color="neutral" mb={1}>
            По желанию — телефон, e-mail или Telegram
          </Typography>
          <Textarea
            placeholder="+375 XX XXX-XX-XX / email / @telegram"
            minRows={1}
            maxRows={2}
            value={contact}
            onChange={(e) => setContact(e.target.value)}
          />
        </Box>

        {state === "error" && (
          <Typography level="body-sm" color="danger">
            Не удалось отправить отзыв. Попробуйте ещё раз.
          </Typography>
        )}

        <Button
          size="lg"
          loading={state === "submitting"}
          disabled={!canSubmit}
          onClick={submit}
        >
          Отправить
        </Button>
      </Stack>
    </Box>
  );
}
